import React, { useState } from "react";
import { Box, FormControl, InputLabel, MenuItem, Modal, Select, Stack } from "@mui/material";
import { ModalWrapper, StyledBackdrop } from "../../../ProductOrder/single/AddProductModal/Styled";
import { TitleText } from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import DateRangeFilter from "../../../../shared/components/FilterOption/DateRangeFilter";
import { ButtonWrapper } from "../../../../shared/components/CancelModal/Styled";
import LoadingButton from "../../../../shared/components/LoadingButton";
import apiMethods from "../../../../utils/api";
import { ColumnMapping, downloadExcel } from "../../../../utils/exportUtils";
import { error } from "../../../../utils/toast";
import {
    ManufacturingScheduleOrder,
    buildProductColumns,
    buildScheduleExportRows,
    buildScheduleGroupRows,
} from "../../../MaterialRequests/Content/ManufacturingScheduleContent/scheduleGrouping";

interface ExportManufacturingScheduleModalProps {
    open: boolean;
    onClose: () => void;
}

const CATEGORY_OPTIONS: { label: string; value: string }[] = [
    { label: "Phuy", value: "Phuy" },
    { label: "Thùng", value: "Thùng" },
    { label: "Cơ Khí Ô Tô", value: "Cơ Khí Ô Tô" },
];

const ExportManufacturingScheduleModal = ({ open, onClose }: ExportManufacturingScheduleModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [category, setCategory] = useState<string>("");
    const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
    const [isLoading, setIsLoading] = useState(false);

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(onClose, 200);
    };


    const handleExport = async () => {
        setIsLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const params: any = {};
            if (category) params.product_category = category;
            if (startDate && endDate) {
                params.due_date_start = startDate;
                params.due_date_end = endDate;
            }

            const res = (await apiMethods.get(
                "/product-flow/v2/orders/manufacturing-schedule/",
                params,
            )) as ManufacturingScheduleOrder[];

            const data = Array.isArray(res) ? res : [];
            const scheduleRows = buildScheduleGroupRows(data);
            const productColumns = buildProductColumns(scheduleRows);

            if (scheduleRows.length === 0) {
                error("Không có dữ liệu để xuất trong phạm vi đã chọn", undefined as any);
                return;
            }

            const columnMapping: ColumnMapping = {};
            columnMapping["due_date"] = "Ngày đến hạn";
            columnMapping["customer_name"] = "Khách hàng";
            columnMapping["order_count"] = "Số đơn hàng";
            columnMapping["order_names"] = "Đơn bán hàng";
            productColumns.forEach((product) => {
                columnMapping[product.key] = product.name;
            });

            const rows = buildScheduleExportRows(scheduleRows, productColumns);

            const filenameParts = ["lich_san_xuat"];
            if (category) {
                const safeCat = category.replace(/\s+/g, "_");
                filenameParts.push(safeCat);
            }
            if (dateRange[0] && dateRange[1]) {
                filenameParts.push(dateRange[0], dateRange[1]);
            }
            const filename = filenameParts.join("_");

            downloadExcel(rows, columnMapping, "Lich San Xuat", filename, false);

            setTimeout(onClose, 100);
        } catch (e: any) {
            error("Xuất lịch sản xuất thất bại", e);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={isOpen} style={{ width: "28vw", maxWidth: 600, minHeight: "auto", padding: "20px" }}>
                <TitleText>Xuất lịch sản xuất</TitleText>
                <Box sx={{ p: 1.5 }}>
                    <Stack spacing={1.5}>
                        <FormControl fullWidth>
                            <InputLabel id="manufacturing-category-label">Loại sản phẩm</InputLabel>
                            <Select
                                labelId="manufacturing-category-label"
                                value={category}
                                label="Loại sản phẩm"
                                onChange={(e) => setCategory(e.target.value as string)}
                                sx={{
                                    height: "42px",
                                    fontSize: "16px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "normal",
                                    fontFamily: "Noto Sans",
                                    borderRadius: "8px",
                                }}
                            >
                                {CATEGORY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <DateRangeFilter onChangeValue={(v: [string | null, string | null]) => setDateRange(v)}
                                         isReset={false} />
                    </Stack>
                </Box>

                <ButtonWrapper style={{ justifyContent: "flex-end", marginTop: "12px" }}>
                    <LoadingButton
                        variant="outlined"
                        onClick={handleCloseWithAnimation}
                        isLoading={false}
                        style={{
                            paddingTop: 8,
                            paddingBottom: 8,
                            display: "flex",
                            justifyContent: "center",
                            width: "60%",
                            minWidth: "120px",
                        }}
                        text={"Đóng"}
                    />
                    <LoadingButton
                        variant="contained"
                        onClick={handleExport}
                        isLoading={isLoading}
                        style={{
                            paddingTop: 8,
                            paddingBottom: 8,
                            display: "flex",
                            justifyContent: "center",
                            width: "60%",
                            minWidth: "120px",
                        }}
                        text={"Xuất Excel"}
                    />
                </ButtonWrapper>
            </ModalWrapper>
        </Modal>
    );
};

export default ExportManufacturingScheduleModal;
